import React, { useState, useEffect } from 'react';
import {
  X,
  Save,
  Loader2,
  AlertCircle,
  CheckCircle2,
  FileText,
  Sparkles
} from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { toast } from 'react-hot-toast';

/**
 * Outline Editor - Edit and refine essay outlines
 *
 * Allows students to customize AI-generated outlines or create their own
 */
export default function OutlineEditor({ outline, onClose, onSave }) {
  const [title, setTitle] = useState(outline.title || '');
  const [strategicAngle, setStrategicAngle] = useState(outline.strategic_angle || '');
  const [outlineData, setOutlineData] = useState(outline.outline_data || {});
  const [themes, setThemes] = useState(outline.themes || []);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Track changes
  useEffect(() => {
    const changed =
      title !== outline.title ||
      strategicAngle !== outline.strategic_angle ||
      JSON.stringify(outlineData) !== JSON.stringify(outline.outline_data);
    setHasChanges(changed);
  }, [title, strategicAngle, outlineData, outline]);

  // Update outline section
  const updateSection = (key, value) => {
    setOutlineData({
      ...outlineData,
      [key]: value
    });
  };

  // Save changes
  const handleSave = async () => {
    try {
      setIsSaving(true);

      const updates = {
        title,
        strategic_angle: strategicAngle,
        outline_data: outlineData,
        themes,
        updated_at: new Date().toISOString(),
        last_edited_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('college_journey_essay_outlines')
        .update(updates)
        .eq('id', outline.id)
        .select()
        .single();

      if (error) throw error;

      console.log('✓ Saved outline changes:', data.id);
      toast.success('Outline saved!');

      if (onSave) {
        onSave(data);
      }

      if (onClose) {
        onClose();
      }

    } catch (err) {
      console.error('Failed to save outline:', err);
      toast.error('Failed to save outline');
    } finally {
      setIsSaving(false);
    }
  };

  // Get outline sections (from AI-generated structure)
  const sections = [
    { key: 'hook', label: 'Opening Hook', placeholder: 'How will you grab the reader\'s attention?' },
    { key: 'context', label: 'Context/Background', placeholder: 'What background does the reader need?' },
    { key: 'rising_action', label: 'Rising Action', placeholder: 'What\'s the journey or progression?' },
    { key: 'turning_point', label: 'Turning Point', placeholder: 'What\'s the moment of change or realization?' },
    { key: 'resolution', label: 'Resolution', placeholder: 'What changed? What did you learn?' },
    { key: 'closing', label: 'Closing', placeholder: 'How will you end with impact?' }
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl max-w-4xl w-full my-8">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-stone-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-violet-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900">Edit Outline</h2>
              <p className="text-sm text-slate-600">Refine your essay structure</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-600" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
          {/* Title */}
          <div>
            <label className="block text-sm font-bold text-slate-900 mb-2">
              Essay Title
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-4 py-3 border-2 border-stone-200 rounded-lg focus:border-violet-500 focus:outline-none text-slate-900"
              placeholder="Give your essay a working title"
            />
          </div>

          {/* Strategic Angle */}
          <div>
            <label className="block text-sm font-bold text-slate-900 mb-2 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-violet-600" />
              Strategic Angle
            </label>
            <textarea
              value={strategicAngle}
              onChange={(e) => setStrategicAngle(e.target.value)}
              rows={3}
              className="w-full px-4 py-3 border-2 border-stone-200 rounded-lg focus:border-violet-500 focus:outline-none text-slate-900 resize-none"
              placeholder="What's the unique essence of your story? What does it reveal about you?"
            />
            <p className="text-xs text-slate-500 mt-1">
              This is your "so what?" - the deeper meaning behind your story
            </p>
          </div>

          {/* Outline Sections */}
          <div className="space-y-4">
            <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wide">
              Essay Structure
            </h3>
            {sections.map(({ key, label, placeholder }) => (
              <div key={key}>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  {label}
                </label>
                <textarea
                  value={outlineData[key] || ''}
                  onChange={(e) => updateSection(key, e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:border-violet-500 focus:outline-none text-slate-900 text-sm resize-none"
                  placeholder={placeholder}
                />
              </div>
            ))}
          </div>

          {/* Help Text */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-900">
                <p className="font-medium mb-1">Editing Tips</p>
                <ul className="space-y-1 text-blue-800">
                  <li>• Keep it concise - these are notes, not full paragraphs</li>
                  <li>• Focus on the key moments and ideas for each section</li>
                  <li>• You'll expand these into full prose when drafting</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-stone-200 bg-stone-50">
          <div className="flex items-center gap-2 text-sm text-slate-600">
            {hasChanges ? (
              <>
                <AlertCircle className="w-4 h-4 text-amber-600" />
                <span className="text-amber-600 font-medium">Unsaved changes</span>
              </>
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                <span className="text-emerald-600">All changes saved</span>
              </>
            )}
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-slate-700 hover:bg-stone-200 rounded-lg transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!hasChanges || isSaving}
              className="px-6 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
